/**
 * Thin REST API wrapper для admin-webapp (owner-кабинет).
 *
 * Auth: тот же flow что и partner — initData → /api/partner/auth/telegram-webapp →
 * JWT с role=owner. Бэк проверяет role на каждом /api/owner/* запросе.
 * Если бэк недоступен (dev) — autoFallback к mock.ts.
 */

import { getWebApp } from './telegram';
import type {
  AdminPartner,
  AdminPlan,
  AdminWithdrawRequest,
  AdminWebhookEvent,
  AdminAuditLog,
  FxRate,
  PlatformBroadcast,
  PartnerMarkupOverride,
  MarkupRule,
  SubscriptionMarkup,
  PayoutDetail,
  SearchResponse,
  ClientDetail,
  OwnerDevice,
  OwnerSubscriptionTraffic,
  PartnerBotBrief,
  PartnerClientBrief,
  OwnerBotDetail,
  OwnerSubscriptionCard,
  OwnerPaymentCard,
  PaymentCheckOutcome,
} from '@/api/types';

const API_BASE = import.meta.env.VITE_API_BASE || '';
// Fail-closed: mock включается ТОЛЬКО явным '=true'. Любое другое значение
// (unset / опечатка / 'False' / '0') = mock выключен. Иначе забытая prod-переменная
// молча подсунула бы фейковые цены/балансы/подписки вместо ошибки.
const USE_MOCK_FALLBACK = import.meta.env.VITE_USE_MOCK_FALLBACK === 'true';

interface AuthState {
  token: string | null;
  isOwner: boolean;
  expiresAt: number;
}

let auth: AuthState = { token: null, isOwner: false, expiresAt: 0 };
let authPromise: Promise<AuthState> | null = null;

class ApiError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message);
  }
}

/** Код ошибки из тела `{error:{code,message}}` — чтобы UI отличал бизнес-отказ от 500. */
export function apiErrorCode(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  return (e.body as { error?: { code?: string } } | null)?.error?.code ?? null;
}

/** Текст отказа из тела `{error:{message}}`. У `ApiError.message` там метод и путь, а не
 *  то, что сказал сервер: «Не удалось» вместо причины не объясняет владельцу ничего. */
export function apiErrorMessage(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  return (e.body as { error?: { message?: string } } | null)?.error?.message ?? null;
}

export class AuthError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Telegram не отдал данные для входа: SDK не загрузился либо initData пустая.
 * Запрос до бэка не уходил, так что «сессия истекла» тут враньё: скорее оборвалась
 * связь или недоступен telegram.org.
 */
export class TelegramUnavailableError extends Error {
  constructor(public reason: 'no_sdk' | 'no_init_data') {
    super(reason === 'no_sdk' ? 'Telegram SDK is not loaded' : 'Telegram initData is empty');
    this.name = 'TelegramUnavailableError';
  }
}

async function authenticate(): Promise<AuthState> {
  if (auth.token && Date.now() < auth.expiresAt - 30_000) return auth;
  if (authPromise) return authPromise;

  authPromise = (async () => {
    const tg = getWebApp();
    if (!tg) throw new TelegramUnavailableError('no_sdk');
    const initData = tg.initData ?? '';
    if (!initData) {
      throw new TelegramUnavailableError('no_init_data');
    }
    const res = await fetch(`${API_BASE}/api/partner/auth/telegram-webapp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ init_data: initData }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new ApiError(res.status, body, `Auth failed: ${res.status}`);
    }
    const data = (await res.json()) as {
      access_token: string;
      expires_in: number;
      is_owner: boolean;
    };
    if (!data.is_owner) {
      throw new ApiError(403, data, 'User is not owner');
    }
    auth = {
      token: data.access_token,
      isOwner: true,
      expiresAt: Date.now() + data.expires_in * 1000,
    };
    return auth;
  })();
  try {
    return await authPromise;
  } finally {
    authPromise = null;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const { token } = await authenticate();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    throw new ApiError(res.status, errBody, `${method} ${path} → ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function tryRequest<T>(method: string, path: string, fallback: () => T, body?: unknown): Promise<T> {
  if (!API_BASE) {
    if (USE_MOCK_FALLBACK) return fallback();
    throw new ApiError(0, null, 'VITE_API_BASE not configured');
  }
  try {
    return await request<T>(method, path, body);
  } catch (e) {
    // Нет данных от Telegram — пробрасываем как есть, это не отказ доступа.
    if (e instanceof TelegramUnavailableError) throw e;
    // 401/403 НИКОГДА не маскируем mock'ом — это бы скрыло истёкший JWT / not-owner юзера
    // и показало бы фейковые данные как настоящие. Бросаем AuthError всегда.
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      throw new AuthError(e.status, e.message);
    }
    if (USE_MOCK_FALLBACK) {
      console.warn('[admin-api] fallback to mock:', path, e);
      return fallback();
    }
    throw e;
  }
}

function noMock(): never {
  throw new ApiError(0, null, 'Нет данных: mock для этой карточки не предусмотрен');
}

interface PromoRaw {
  id: string;
  code: string;
  partner_id: string | null;
  discount_type: 'percent' | 'fixed';
  discount_basis_points: number | null;
  discount_fixed_usd_minor: number | null;
  max_uses: number | null;
  max_uses_per_client: number;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  created_at: string;
  redemption_count: number;
}

export interface AdminPromo {
  id: string;
  code: string;
  partnerId: string | null;
  discountType: 'percent' | 'fixed';
  discountBasisPoints: number | null;
  discountFixedUsdMinor: number | null;
  maxUses: number | null;
  maxUsesPerClient: number;
  validUntil: string | null;
  isActive: boolean;
  createdAt: string;
  redemptionCount: number;
}

export interface OwnerReferralStats {
  totalReferred: number;
  lockedReferred: number;
  totalBonusPaidMinor: number;
  topReferrers: Array<{
    clientId: string;
    tgUserId: number;
    tgUsername: string | null;
    referredCount: number;
    bonusEarnedMinor: number;
  }>;
}

function mapPromo(p: PromoRaw): AdminPromo {
  return {
    id: p.id,
    code: p.code,
    partnerId: p.partner_id,
    discountType: p.discount_type,
    discountBasisPoints: p.discount_basis_points,
    discountFixedUsdMinor: p.discount_fixed_usd_minor,
    maxUses: p.max_uses,
    maxUsesPerClient: p.max_uses_per_client,
    validUntil: p.valid_until,
    isActive: p.is_active,
    createdAt: p.created_at,
    redemptionCount: p.redemption_count,
  };
}

export const api = {
  // VPN Reseller API: доступ партнёра и баланс поставщика
  setPartnerApiEnabled: (id: string, enabled: boolean) =>
    request<{ partner_id: string; api_enabled: boolean }>(
      'POST',
      `/api/owner/partners/${id}/api-enabled`,
      { enabled },
    ),
  adaptgroupBalance: () =>
    request<{
      balance: string | null;
      currency: string;
      balance_updated_at: string | null;
      checked_at: string | null;
      threshold_usd: number;
    }>('GET', '/api/owner/adaptgroup/balance'),
  getAuth: () => auth,

  // Backend: /api/owner/stats/network возвращает period_days, gross_revenue_minor,
  // platform_revenue_minor, partners_count, active_subscriptions, ... (всё в USD minor).
  ownerNetworkStats: () =>
    tryRequest<{
      period_days: number;
      gross_revenue_minor: number;
      platform_revenue_minor: number;
      adaptgroup_cost_minor: number;
      partner_revenue_minor: number;
      new_subscriptions: number;
      expired_subscriptions: number;
      active_subscriptions: number;
      total_clients: number;
      partners_count: number;
      avg_check_minor: number;
      currency: string;
    }>('GET', '/api/owner/stats/network', () => ({
      period_days: 30,
      gross_revenue_minor: 0,
      platform_revenue_minor: 0,
      adaptgroup_cost_minor: 0,
      partner_revenue_minor: 0,
      new_subscriptions: 0,
      expired_subscriptions: 0,
      active_subscriptions: 0,
      total_clients: 0,
      partners_count: 0,
      avg_check_minor: 0,
      currency: 'USD',
    })),

  // Backend snake_case PartnerRankingResponse. Возвращаем raw — endpoint пока не используется в UI.
  ownerPartnerRankings: () =>
    tryRequest<
      Array<{
        partner_id: string;
        display_name: string;
        is_owner: boolean;
        gross_revenue_minor: number;
        platform_markup_minor: number;
        sales_count: number;
        active_subscriptions: number;
      }>
    >('GET', '/api/owner/stats/partners', () => []),

  // Честные сетевые счётчики (COUNT на бэке) — НЕ длина пагинированного списка.
  networkStats: () =>
    tryRequest<{
      active_partners: number;
      active_subscriptions: number;
      total_clients: number;
      partners_count: number;
    }>('GET', '/api/owner/stats/network', () => ({
      active_partners: 0,
      active_subscriptions: 0,
      total_clients: 0,
      partners_count: 0,
    })),

  ownerPnl: async (): Promise<{ chart: Array<{ day: string; gmv: number; margin: number }> }> => {
    // Backend: /api/owner/stats/pnl возвращает {granularity, period_days, buckets[{period_start, gross_revenue_minor, platform_revenue_minor, ...}]}.
    // Transform: cents → RUB (×1 при курсе 100), period_start → 'day' MM-DD.
    const raw = await tryRequest<{
      granularity: string;
      period_days: number;
      buckets: Array<{
        period_start: string;
        gross_revenue_minor: number;
        platform_revenue_minor: number;
        adaptgroup_cost_minor: number;
        partner_revenue_minor: number;
        sales_count: number;
      }>;
    }>('GET', '/api/owner/stats/pnl?period_days=14&granularity=day', () => ({
      granularity: 'day',
      period_days: 14,
      buckets: [],
    }));
    return {
      chart: raw.buckets.map((b) => {
        const d = new Date(b.period_start);
        return {
          day: `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
          gmv: b.gross_revenue_minor,
          margin: b.platform_revenue_minor,
        };
      }),
    };
  },

  // TODO бэка: список партнёров через поиск (есть только rankings — фильтр на клиенте)
  partners: async (): Promise<AdminPartner[]> => {
    // Backend возвращает snake_case + cents — transform под наш camelCase + rub.
    const raw = await tryRequest<Array<{
      id: string;
      tg_id: number | null;
      username: string | null;
      first_name: string;
      status: string;
      created_at: string;
      active_subs: number;
      bots_count: number;
      total_earned_minor: number;
      balance_usd_minor: number;
    }>>('GET', '/api/owner/partners?limit=500', () => []);
    return raw.map((p) => ({
      id: p.id,
      tgId: p.tg_id ?? 0,
      username: p.username,
      firstName: p.first_name,
      status: p.status as AdminPartner['status'],
      createdAt: p.created_at,
      activeSubs: p.active_subs,
      botsCount: p.bots_count,
      // USD-cents как есть; конверсия в ₽ — в компоненте по живому курсу CBR (perRub).
      totalEarnedUsdMinor: p.total_earned_minor,
      balanceUsdMinor: p.balance_usd_minor,
    }));
  },

  banPartner: (partnerId: string, banned: boolean) =>
    tryRequest<{ status: string }>(
      'POST',
      `/api/owner/partners/${partnerId}/${banned ? 'ban' : 'unban'}`,
      () => ({ status: banned ? 'banned' : 'active' }),
    ),

  partnerOverride: (partnerId: string, body: { markup_cap_percent: number | null; markup_floor_percent: number | null }) =>
    tryRequest<PartnerMarkupOverride>(
      'POST',
      `/api/owner/partners/${partnerId}/markup-override`,
      () => ({
        partnerId,
        markupCapPercent: body.markup_cap_percent,
        markupFloorPercent: body.markup_floor_percent,
      }),
      body,
    ),

  // Backend snake_case PendingPayoutResponse → transform в AdminWithdrawRequest.
  // amount_usd_minor (USD cents) как есть — в ₽ конвертит компонент по живому курсу.
  pendingWithdraws: async (): Promise<AdminWithdrawRequest[]> => {
    const raw = await tryRequest<
      Array<{
        id: string;
        partner_id: string;
        partner_name: string;
        amount_usd_minor: number;
        method: string;
        destination: string;
        status: string;
        note: string | null;
        created_at: string;
      }>
    >('GET', '/api/owner/payouts/pending', () => []);
    return raw.map((p) => ({
      id: p.id,
      partnerId: p.partner_id,
      partnerName: p.partner_name,
      amountUsdMinor: p.amount_usd_minor,
      method: p.method as AdminWithdrawRequest['method'],
      createdAt: p.created_at,
    }));
  },

  decideWithdraw: (id: string, approve: boolean) =>
    tryRequest<{ status: string }>(
      'POST',
      `/api/owner/payouts/${id}/${approve ? 'approve' : 'decline'}`,
      () => ({ status: approve ? 'approved' : 'declined' }),
    ),

  // Block F.3: detail для admin /withdrawals/$id
  payoutDetail: (id: string) =>
    tryRequest<PayoutDetail>('GET', `/api/owner/payouts/${id}`, () => ({
      id,
      partner_id: 'mock_partner',
      partner_name: 'Mock Partner',
      partner_tg_username: null,
      partner_tg_id: null,
      amount_usd_minor: 5000,
      method: 'cryptobot',
      destination: 'TQz...mock',
      status: 'pending',
      note: null,
      rejection_reason: null,
      auto_executed: false,
      external_ref: null,
      auto_error: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      processed_at: null,
      completed_at: null,
      fee_usd_minor: 150,
      net_usd_minor: 4850,
      net_display_minor: 393900,
      send_in_display: false,
      display_rate: '81.2',
      display_currency: 'RUB',
    })),

  // Завершить вывод (деньги отправлены): holding → expense. Раньше только из бота.
  completePayout: (id: string) =>
    tryRequest<PayoutDetail>('POST', `/api/owner/payouts/${id}/complete`, () => ({
      id,
      partner_id: 'mock_partner',
      partner_name: 'Mock Partner',
      partner_tg_username: null,
      partner_tg_id: null,
      amount_usd_minor: 5000,
      method: 'cryptobot',
      destination: 'TQz...mock',
      status: 'completed',
      note: null,
      rejection_reason: null,
      auto_executed: false,
      external_ref: null,
      auto_error: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      processed_at: new Date().toISOString(),
      completed_at: new Date().toISOString(),
      fee_usd_minor: 150,
      net_usd_minor: 4850,
      net_display_minor: 393900,
      send_in_display: false,
      display_rate: '81.2',
      display_currency: 'RUB',
    })),

  // Повторить авто-выплату через CryptoBot для застрявшего PENDING.
  retryPayout: (id: string) =>
    tryRequest<{
      id: string;
      ok: boolean;
      status: string;
      external_ref: string | null;
      error: string | null;
    }>('POST', `/api/owner/payouts/${id}/retry-auto`, () => ({
      id,
      ok: false,
      status: 'pending',
      external_ref: null,
      error: 'mock',
    })),

  // Лента всех заявок (опц. фильтр по статусу) — completed/rejected не исчезают.
  payoutHistory: async (status?: string): Promise<AdminWithdrawRequest[]> => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const raw = await tryRequest<
      Array<{
        id: string;
        partner_id: string;
        partner_name: string;
        amount_usd_minor: number;
        method: string;
        destination: string;
        status: string;
        note: string | null;
        created_at: string;
      }>
    >('GET', `/api/owner/payouts/history${qs}`, () => []);
    return raw.map((p) => ({
      id: p.id,
      partnerId: p.partner_id,
      partnerName: p.partner_name,
      amountUsdMinor: p.amount_usd_minor,
      method: p.method as AdminWithdrawRequest['method'],
      status: p.status,
      createdAt: p.created_at,
    }));
  },

  // Block F.4: GET /api/owner/partners/{id}
  partnerDetail: (id: string) =>
    tryRequest<{
      id: string;
      tg_user_id: number | null;
      display_name: string;
      is_active: boolean;
      is_owner: boolean;
      bot_limit: number;
      bots_count: number;
      active_subscriptions: number;
      revenue_minor: number;
      margin_minor: number;
      balance_usd_minor: number;
      onboarding_completed: boolean;
      api_enabled: boolean;
      has_api_key: boolean;
      created_at: string;
    }>('GET', `/api/owner/partners/${id}`, () => ({
      id,
      tg_user_id: null,
      display_name: 'Mock Partner',
      is_active: true,
      is_owner: false,
      bot_limit: 5,
      bots_count: 1,
      active_subscriptions: 10,
      revenue_minor: 100000,
      margin_minor: 30000,
      balance_usd_minor: 5000,
      onboarding_completed: true,
      api_enabled: true,
      has_api_key: false,
      created_at: new Date().toISOString(),
    })),

  // Block F.5: GET /api/owner/clients/{id}
  clientDetail: (id: string) =>
    tryRequest<ClientDetail>('GET', `/api/owner/clients/${id}`, () => ({
      id,
      tg_user_id: 0,
      tg_username: null,
      locale: 'ru',
      is_active: true,
      is_blocked: false,
      created_at: new Date().toISOString(),
      partner_id: 'mock',
      partner_display_name: null,
      utm_source: null,
      utm_campaign: null,
      utm_medium: null,
      referrer_id: null,
      referrer_tg_username: null,
      referred_count: 0,
      balance_usd_minor: 0,
      subscriptions: [],
      ledger: [],
      payments: [],
    })),

  // Глобальный поиск: клиенты / боты / партнёры.
  search: (q: string) =>
    tryRequest<SearchResponse>(
      'GET',
      `/api/owner/search?q=${encodeURIComponent(q)}`,
      () => ({ clients: [], bots: [], partners: [] }),
    ),

  banClient: (clientId: string) =>
    tryRequest<{ client_id: string; is_active: boolean }>(
      'POST',
      `/api/owner/clients/${clientId}/ban`,
      () => ({ client_id: clientId, is_active: false }),
    ),

  unbanClient: (clientId: string) =>
    tryRequest<{ client_id: string; is_active: boolean }>(
      'POST',
      `/api/owner/clients/${clientId}/unban`,
      () => ({ client_id: clientId, is_active: true }),
    ),

  // Карточки подписки и платежа для саппорта. Без mock: выдуманный статус подписки или
  // «оплачено» у счёта хуже честной ошибки, поэтому запасной вариант бросает.
  subscriptionCard: (subId: string) =>
    tryRequest<OwnerSubscriptionCard>('GET', `/api/owner/subscriptions/${subId}`, noMock),
  paymentCard: (paymentId: string) =>
    tryRequest<OwnerPaymentCard>('GET', `/api/owner/payments/${paymentId}`, noMock),
  // Действия только через request: mock-успех тут был бы прямым враньём.
  checkPayment: (paymentId: string, reason: string) =>
    request<{ outcome: PaymentCheckOutcome; status: string; detail: string | null }>(
      'POST',
      `/api/owner/payments/${paymentId}/check`,
      { reason },
    ),
  resolvePayment: (paymentId: string, reason: string) =>
    request<OwnerPaymentCard>('POST', `/api/owner/payments/${paymentId}/resolve`, { reason }),
  rotateSubscriptionLink: (subId: string, reason: string) =>
    request<{ subscription_url: string; link_rotated_at: string | null; blocks_cleared: number }>(
      'POST',
      `/api/owner/subscriptions/${subId}/rotate-link`,
      { reason },
    ),
  resetSubscriptionDevices: (subId: string, reason: string) =>
    request<{ devices_deleted: number; devices_failed: number; blocks_cleared: number }>(
      'POST',
      `/api/owner/subscriptions/${subId}/devices/reset`,
      { reason },
    ),

  // POST /api/owner/clients/subscriptions/{id}/transfer — переставить подписку на
  // другой Telegram-аккаунт. request (не tryRequest): действие необратимое, и mock-успех
  // тут был бы прямым враньём.
  transferSubscription: (
    subId: string,
    body: {
      to_tg_user_id: number;
      to_tg_username?: string | null;
      revoke_devices: boolean;
      reason: string;
    },
  ) =>
    request<{
      subscription_id: string;
      to_client_id: string;
      to_client_created: boolean;
      devices_revoked: number;
      link_rotated: boolean;
      warnings: string[];
    }>('POST', `/api/owner/clients/subscriptions/${subId}/transfer`, body),

  freezeSubscription: (subId: string) =>
    tryRequest<{ subscription_id: string; is_frozen: boolean; end_date: string }>(
      'POST',
      `/api/owner/clients/subscriptions/${subId}/freeze`,
      () => ({ subscription_id: subId, is_frozen: true, end_date: new Date().toISOString() }),
    ),

  unfreezeSubscription: (subId: string) =>
    tryRequest<{ subscription_id: string; is_frozen: boolean; end_date: string }>(
      'POST',
      `/api/owner/clients/subscriptions/${subId}/unfreeze`,
      () => ({ subscription_id: subId, is_frozen: false, end_date: new Date().toISOString() }),
    ),

  subscriptionDevices: (subId: string) =>
    tryRequest<OwnerDevice[]>(
      'GET',
      `/api/owner/clients/subscriptions/${subId}/devices`,
      () => [],
    ),

  subscriptionTraffic: (subId: string) =>
    tryRequest<OwnerSubscriptionTraffic>(
      'GET',
      `/api/owner/clients/subscriptions/${subId}/traffic`,
      () => ({ used_traffic_bytes: null, traffic_limit_bytes: null }),
    ),

  // Drill-down: боты партнёра + клиенты (опц. по конкретному боту).
  partnerBots: (partnerId: string) =>
    tryRequest<PartnerBotBrief[]>(
      'GET',
      `/api/owner/partners/${partnerId}/bots`,
      () => [],
    ),

  partnerClients: (partnerId: string, botId?: string) =>
    tryRequest<PartnerClientBrief[]>(
      'GET',
      `/api/owner/partners/${partnerId}/clients${botId ? `?bot_id=${botId}` : ''}`,
      () => [],
    ),

  // Управление ботом (owner).
  botDetail: (botId: string) =>
    tryRequest<OwnerBotDetail>('GET', `/api/owner/bots/${botId}`, () => ({
      id: botId,
      username: 'mockbot',
      title: 'Mock Bot',
      status: 'ACTION',
      is_active: true,
      partner_id: 'mock',
      partner_name: null,
      clients_count: 0,
      active_subscriptions: 0,
      created_at: new Date().toISOString(),
      deleted_at: null,
    })),

  botAction: (botId: string, action: 'stop' | 'start' | 'ban' | 'unban') =>
    tryRequest<OwnerBotDetail>(
      'POST',
      `/api/owner/bots/${botId}/${action}`,
      () => ({
        id: botId,
        username: 'mockbot',
        title: 'Mock Bot',
        status: action === 'stop' ? 'STOP' : action === 'ban' ? 'BANNED' : 'ACTION',
        is_active: action === 'start' || action === 'unban',
        partner_id: 'mock',
        partner_name: null,
        clients_count: 0,
        active_subscriptions: 0,
        created_at: new Date().toISOString(),
        deleted_at: null,
      }),
    ),

  // Block C.1: ручной топап баланса клиенту
  topupClient: (clientId: string, amount_usd_minor: number, reason: string, note?: string) =>
    tryRequest<{
      client_id: string;
      amount_usd_minor: number;
      new_balance_minor: number;
      tx_id: string;
    }>(
      'POST',
      `/api/owner/clients/${clientId}/topup`,
      () => ({
        client_id: clientId,
        amount_usd_minor,
        new_balance_minor: amount_usd_minor,
        tx_id: 'mock',
      }),
      { amount_usd_minor, reason, note },
    ),

  // Block G.1: cohort P&L
  cohortPnl: (months = 6) =>
    tryRequest<Array<{
      cohort_month: string;
      plan_name: string;
      plan_uuid: string;
      subs_count: number;
      partner_margin_minor: number;
      platform_margin_minor: number;
      total_revenue_minor: number;
    }>>('GET', `/api/owner/analytics/cohort-pnl?months=${months}`, () => []),

  // Финансовая разбивка за период с разбивкой по платёжкам (Ф3).
  // Зеркало inline `/admin → 💰 Финансы`. Цифры считаются тем же агрегатором.
  ownerFinanceBreakdown: (periodDays = 30) =>
    tryRequest<{
      period_days: number;
      gross_revenue_minor: number;
      platform_margin_minor: number;
      partner_margin_minor: number;
      adaptgroup_cost_minor: number;
      payment_fees_usd_minor: number;
      net_profit_minor: number;
      providers: Array<{
        provider: string;
        paid_count: number;
        failed_count: number;
        revenue_usd_minor: number;
        fee_usd_minor: number;
      }>;
    }>(
      'GET',
      `/api/owner/analytics/finance-breakdown?period_days=${periodDays}`,
      () => ({
        period_days: periodDays,
        gross_revenue_minor: 0,
        platform_margin_minor: 0,
        partner_margin_minor: 0,
        adaptgroup_cost_minor: 0,
        payment_fees_usd_minor: 0,
        net_profit_minor: 0,
        providers: [],
      }),
    ),

  // Phase 4 follow-up: cohort-retention. Возвращает sign-ups per неделя + кол-во
  // активных через 7/30 дней.
  retention: (weeks = 8) =>
    tryRequest<{
      weeks: number;
      cohorts: Array<{
        cohort_week: string;
        signups: number;
        active_d7: number;
        active_d30: number;
      }>;
      overall_d7_percent: number;
      overall_d30_percent: number;
    }>('GET', `/api/owner/analytics/retention?weeks=${weeks}`, () => ({
      weeks,
      cohorts: [],
      overall_d7_percent: 0,
      overall_d30_percent: 0,
    })),

  // Phase 4 follow-up: GET текущих markup overrides для prefill UI-формы.
  partnerOverrideGet: (partnerId: string) =>
    tryRequest<{
      partner_id: string;
      markup_cap_percent: number | null;
      markup_floor_percent: number | null;
    }>(
      'GET',
      `/api/owner/partners/${partnerId}/markup-override`,
      () => ({
        partner_id: partnerId,
        markup_cap_percent: null,
        markup_floor_percent: null,
      }),
    ),

  // Платформенные планы. Backend: /api/owner/plans возвращает snake_case + cents,
  // transform в camelCase + rub под AdminPlan тип. FX 100 RUB/USD = pricing default.
  plans: async (): Promise<AdminPlan[]> => {
    const raw = await tryRequest<Array<{
      id: string;
      name: string;
      days: number;
      devices: number;
      traffic_gb: number | null;
      base_price_minor: number;
      floor_minor: number;
      visible: boolean;
      is_trial: boolean;
      is_special?: boolean;
      fixed_retail_display_minor?: number | null;
    }>>('GET', '/api/owner/plans', () => []);
    // cents → RUB: cents * 1 (default rate 100 RUB/USD → 1 cent = 1 RUB)
    return raw.map((p) => ({
      id: p.id,
      name: p.name,
      days: p.days,
      devices: p.devices,
      trafficGb: p.traffic_gb,
      basePriceRub: p.base_price_minor,
      floorRub: p.floor_minor,
      visible: p.visible,
      isSpecial: p.is_special ?? false,
      // Копейки рубля → рубли. Тут именно рубли, а не USD-центы: цена спецтарифа
      // задана в display-валюте и от курса не зависит.
      fixedRetailRub:
        p.fixed_retail_display_minor != null ? p.fixed_retail_display_minor / 100 : null,
    }));
  },

  // base/floor приходят синком AdaptGroup и не редактируются. Редактируемое:
  // `visible`, а также спец-режим тарифа (`is_special` + жёсткая рублёвая цена).
  updatePlan: (
    id: string,
    body: {
      visible?: boolean;
      is_special?: boolean;
      fixed_retail_display_minor?: number | null;
    },
  ) => tryRequest('PATCH', `/api/owner/plans/${id}`, () => null, body),

  // ── Промокоды (owner CRUD) ──────────────────────────────────────────────
  promos: async (): Promise<AdminPromo[]> => {
    const raw = await tryRequest<PromoRaw[]>('GET', '/api/owner/promos', () => []);
    return raw.map(mapPromo);
  },
  createPromo: async (body: {
    code: string;
    discount_type: 'percent' | 'fixed';
    discount_basis_points?: number | null;
    discount_fixed_usd_minor?: number | null;
    max_uses?: number | null;
    max_uses_per_client?: number;
    valid_until?: string | null;
    is_active?: boolean;
  }): Promise<AdminPromo> => {
    const raw = await request<PromoRaw>('POST', '/api/owner/promos', body);
    return mapPromo(raw);
  },
  patchPromo: async (
    id: string,
    body: { is_active?: boolean; max_uses?: number | null; clear_max_uses?: boolean },
  ): Promise<AdminPromo> => {
    const raw = await request<PromoRaw>('PATCH', `/api/owner/promos/${id}`, body);
    return mapPromo(raw);
  },
  deletePromo: (id: string) =>
    request<void>('DELETE', `/api/owner/promos/${id}`),

  // ── Личные сообщения (owner → партнёр/клиент) ───────────────────────────
  messagePartner: (id: string, text: string) =>
    request<{ ok: boolean }>('POST', `/api/owner/partners/${id}/message`, { text }),
  messageClient: (id: string, text: string) =>
    request<{ ok: boolean }>('POST', `/api/owner/clients/${id}/message`, { text }),

  // ── GDPR: анонимизация клиента ───────────────────────────────────────────
  anonymizeClient: (id: string) =>
    request<{ ok: boolean }>('POST', `/api/owner/clients/${id}/anonymize`),

  // ── Сетевая реферальная статистика ───────────────────────────────────────
  ownerReferralStats: async (): Promise<OwnerReferralStats> => {
    const raw = await tryRequest<{
      total_referred: number;
      locked_referred: number;
      total_bonus_paid_minor: number;
      currency: string;
      top_referrers: Array<{
        client_id: string;
        tg_user_id: number;
        tg_username: string | null;
        referred_count: number;
        bonus_earned_minor: number;
      }>;
    }>('GET', '/api/owner/stats/referrals', () => ({
      total_referred: 0,
      locked_referred: 0,
      total_bonus_paid_minor: 0,
      currency: 'USD',
      top_referrers: [],
    }));
    return {
      totalReferred: raw.total_referred,
      lockedReferred: raw.locked_referred,
      totalBonusPaidMinor: raw.total_bonus_paid_minor,
      topReferrers: raw.top_referrers.map((t) => ({
        clientId: t.client_id,
        tgUserId: t.tg_user_id,
        tgUsername: t.tg_username,
        referredCount: t.referred_count,
        bonusEarnedMinor: t.bonus_earned_minor,
      })),
    };
  },

  // ── CSV-экспорт (скачивание blob c Bearer) ───────────────────────────────
  exportCsv: async (kind: 'subscriptions' | 'partners' | 'pnl'): Promise<void> => {
    const { token } = await authenticate();
    const res = await fetch(`${API_BASE}/api/owner/export/${kind}.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`export ${kind} failed: ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  // Backend: OwnerWebhookResponse {id, source, event, status, received_at, attempts}.
  webhookEvents: async (): Promise<AdminWebhookEvent[]> => {
    const raw = await tryRequest<
      Array<{ id: string; source: string; event: string; status: string; received_at: string; attempts: number }>
    >('GET', '/api/owner/webhooks', () => []);
    // Бэк-статусы webhook_events: received/processing/processed/failed/dead.
    // Фронт знает ok/failed/retrying. Без маппинга 'processed' падал в дефолт →
    // жёлтый крутящийся спиннер, хотя событие давно обработано.
    const mapStatus = (s: string): AdminWebhookEvent['status'] => {
      if (s === 'processed') return 'ok';
      if (s === 'failed' || s === 'dead') return 'failed';
      return 'retrying'; // received / processing
    };
    return raw.map((e) => ({
      id: e.id,
      source: e.source as AdminWebhookEvent['source'],
      event: e.event,
      status: mapStatus(e.status),
      receivedAt: e.received_at,
      attempts: e.attempts,
    }));
  },

  retryWebhook: (id: string) =>
    tryRequest<{ ok: boolean }>('POST', `/api/owner/webhooks/${id}/retry`, () => ({ ok: true })),

  // Backend: AuditEntryResponse {id, actor_tg_id, actor_name, action, target, timestamp}.
  auditLog: async (): Promise<AdminAuditLog[]> => {
    const raw = await tryRequest<
      Array<{ id: string; actor_tg_id: number; actor_name: string; action: string; target: string; timestamp: string }>
    >('GET', '/api/owner/audit', () => []);
    return raw.map((a) => ({
      id: a.id,
      actorTgId: a.actor_tg_id,
      actorName: a.actor_name,
      action: a.action,
      target: a.target,
      timestamp: a.timestamp,
    }));
  },

  // Backend: FxRateResponse {code, per_usd, manual, updated_at, source}.
  // Frontend type использует `perRub` (исторически назван так в FxRate). Маппим per_usd → perRub.
  fxRates: async (): Promise<FxRate[]> => {
    const raw = await tryRequest<
      Array<{ code: string; per_usd: number; manual: boolean; updated_at: string; source: string }>
    >('GET', '/api/owner/fx/rates', () => []);
    return raw.map((r) => ({
      code: r.code,
      perRub: r.per_usd,
      manual: r.manual,
      updatedAt: r.updated_at,
      source: r.source as FxRate['source'],
    }));
  },

  refreshFxFromCbr: () =>
    tryRequest('POST', '/api/owner/fx/refresh-cbr', () => ({ ok: true })),

  // Backend expects `per_usd` body, returns FxRateResponse.
  updateFxRate: async (code: string, perRub: number): Promise<FxRate> => {
    const raw = await tryRequest<{ code: string; per_usd: number; manual: boolean; updated_at: string; source: string }>(
      'PATCH',
      `/api/owner/fx/rates/${code}`,
      () => ({
        code,
        per_usd: perRub,
        manual: true,
        updated_at: new Date().toISOString(),
        source: 'manual',
      }),
      { per_usd: perRub },
    );
    return {
      code: raw.code,
      perRub: raw.per_usd,
      manual: raw.manual,
      updatedAt: raw.updated_at,
      source: raw.source as FxRate['source'],
    };
  },

  // Backend: PlatformBroadcastResponse snake_case.
  platformBroadcasts: async (): Promise<PlatformBroadcast[]> => {
    const raw = await tryRequest<
      Array<{
        id: string;
        title: string;
        body: string;
        audience: string;
        status: string;
        scheduled_at: string | null;
        total_count: number;
        sent_count: number;
        failed_count: number;
        message_preview: string | null;
        created_at: string;
        recurrence: string | null;
        is_recurring_active: boolean;
        next_fire_at: string | null;
        fire_count: number;
      }>
    >('GET', '/api/owner/broadcasts', () => []);
    return raw.map((b) => ({
      id: b.id,
      title: b.title,
      body: b.body,
      audience: b.audience as PlatformBroadcast['audience'],
      status: b.status as PlatformBroadcast['status'],
      scheduledAt: b.scheduled_at,
      totalCount: b.total_count,
      sentCount: b.sent_count,
      failedCount: b.failed_count,
      messagePreview: b.message_preview,
      createdAt: b.created_at,
      recurrence: b.recurrence,
      isRecurringActive: Boolean(b.is_recurring_active),
      nextFireAt: b.next_fire_at,
      fireCount: b.fire_count ?? 0,
    }));
  },

  /** Остановить платформенную рассылку и снять расписание. */
  cancelPlatformBroadcast: (id: string) =>
    request<{ id: string; status: string; is_recurring_active: boolean }>(
      'POST',
      `/api/owner/broadcasts/${id}/cancel`,
    ),

  createPlatformBroadcast: async (body: {
    title: string;
    body: string;
    audience: PlatformBroadcast['audience'];
    recurrence?: 'once' | 'daily' | 'weekly' | null;
    scheduled_at?: string | null;
    time_of_day?: string | null;
    weekday?: number | null;
    protect_content?: boolean;
    message_effect_id?: string | null;
    rate_per_second?: number | null;
    inline_buttons?: Array<Array<{ text: string; url?: string; kind?: 'url' | 'action'; action?: string; style?: 'primary' | 'success' | 'danger' }>> | null;
    media_type?: string | null;
    media_url?: string | null;
  }): Promise<PlatformBroadcast> => {
    const raw = await tryRequest<{
      id: string;
      title: string;
      body: string;
      audience: string;
      status: string;
      scheduled_at: string | null;
      total_count: number;
      sent_count: number;
      failed_count: number;
      message_preview: string | null;
      created_at: string;
      recurrence: string | null;
      is_recurring_active: boolean;
      next_fire_at: string | null;
      fire_count: number;
    }>(
      'POST',
      '/api/owner/broadcasts',
      () => ({
        id: `pb_${Date.now()}`,
        title: body.title,
        body: body.body,
        audience: body.audience,
        status: 'sent',
        scheduled_at: null,
        total_count: 0,
        sent_count: 0,
        failed_count: 0,
        message_preview: null,
        created_at: new Date().toISOString(),
        recurrence: body.recurrence ?? null,
        is_recurring_active: Boolean(body.recurrence),
        next_fire_at: null,
        fire_count: 0,
      }),
      body,
    );
    return {
      id: raw.id,
      title: raw.title,
      body: raw.body,
      audience: raw.audience as PlatformBroadcast['audience'],
      status: raw.status as PlatformBroadcast['status'],
      scheduledAt: raw.scheduled_at,
      totalCount: raw.total_count,
      sentCount: raw.sent_count,
      failedCount: raw.failed_count,
      messagePreview: raw.message_preview,
      createdAt: raw.created_at,
      recurrence: raw.recurrence,
      isRecurringActive: Boolean(raw.is_recurring_active),
      nextFireAt: raw.next_fire_at,
      fireCount: raw.fire_count ?? 0,
    };
  },

  broadcastAudienceSize: (audience: PlatformBroadcast['audience']) =>
    tryRequest<{ audience: string; count: number }>(
      'GET',
      `/api/owner/broadcasts/audience-size?audience=${encodeURIComponent(audience)}`,
      () => ({ audience, count: 0 }),
    ),

  // Ф5: живой прогресс платформенной рассылки.
  platformBroadcastLive: (broadcastId: string) =>
    tryRequest<{
      status: string;
      sent: number;
      failed: number;
      total: number;
      rate_per_second: number | null;
      computed_speed: number | null;
      eta_seconds: number | null;
      tail: Array<{ tg_user_id: number; username: string | null; ok: boolean; ts: number }>;
      log_available?: boolean;
    }>('GET', `/api/owner/broadcasts/${broadcastId}/live`, () => ({
      status: 'sent',
      sent: 0,
      failed: 0,
      total: 0,
      rate_per_second: null,
      computed_speed: null,
      eta_seconds: null,
      tail: [],
    })),

  // Ф6: тест-отправка черновика платформенной рассылки owner'у в ЛС.
  testPlatformBroadcast: async (draft: {
    title: string;
    body: string;
    audience: string;
    media_type?: string | null;
    media_url?: string | null;
    inline_buttons?: Array<Array<{ text: string; url?: string; kind?: 'url' | 'action'; action?: string; style?: 'primary' | 'success' | 'danger' }>> | null;
    protect_content?: boolean;
    message_effect_id?: string | null;
  }): Promise<{ sent: boolean }> => request('POST', '/api/owner/broadcasts/test', draft),

  // Ф7: загрузка медиа платформенной рассылки.
  uploadPlatformMedia: async (file: File): Promise<{ media_type: string; media_url: string }> => {
    if (!API_BASE) {
      throw new ApiError(0, null, 'VITE_API_BASE not configured — media uploads need real backend');
    }
    const { token } = await authenticate();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/api/owner/broadcasts/media`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new ApiError(res.status, err, `uploadPlatformMedia → ${res.status}`);
    }
    return res.json();
  },

  // ---------- System settings ----------

  systemSettings: () =>
    tryRequest<
      Array<{
        key: string;
        value: unknown;
        description: string | null;
        updated_at: string;
        updated_by_tg_id: number | null;
      }>
    >('GET', '/api/owner/system-settings', () => []),

  updateSystemSetting: (key: string, value: unknown) =>
    tryRequest(
      'PATCH',
      `/api/owner/system-settings/${key}`,
      () => ({ key, value, description: null, updated_at: new Date().toISOString(), updated_by_tg_id: null }),
      { value },
    ),

  // ---------- Платформенная наценка (markup) ----------

  // C: глобальная + per-plan наценка на подписки (platform_pricing).
  subscriptionMarkup: () =>
    tryRequest<SubscriptionMarkup>(
      'GET',
      '/api/owner/pricing/subscriptions',
      () => ({
        global_markup_percent: 30,
        global_markup_fixed_usd_minor: null,
        per_plan: [],
        plans: [],
        example_plan_name: null,
        example_cost_usd_minor: null,
        example_platform_usd_minor: null,
      }),
    ),

  setGlobalSubscriptionMarkup: (body: MarkupRule) =>
    tryRequest<SubscriptionMarkup>('PUT', '/api/owner/pricing/subscriptions/global', () => null as never, body),

  setPlanSubscriptionMarkup: (planId: string, body: MarkupRule) =>
    tryRequest<SubscriptionMarkup>('PUT', `/api/owner/pricing/subscriptions/plan/${planId}`, () => null as never, body),

  deletePlanSubscriptionMarkup: (planId: string) =>
    tryRequest<SubscriptionMarkup>('DELETE', `/api/owner/pricing/subscriptions/plan/${planId}`, () => null as never),

  // Абсолютная цена плана (platform_pricing.fixed_price_usd_minor). USD-центы.
  setPlanSubscriptionPrice: (planId: string, priceUsdMinor: number) =>
    tryRequest<SubscriptionMarkup>(
      'PUT',
      `/api/owner/pricing/subscriptions/plan/${planId}/price`,
      () => null as never,
      { price_usd_minor: priceUsdMinor },
    ),

  deletePlanSubscriptionPrice: (planId: string) =>
    tryRequest<SubscriptionMarkup>(
      'DELETE',
      `/api/owner/pricing/subscriptions/plan/${planId}/price`,
      () => null as never,
    ),

  // A: платформенная наценка на трафик/GB (system_settings, basis points).
  trafficMarkup: () =>
    tryRequest<{ markup_bp: number; markup_percent: number; source: string }>(
      'GET',
      '/api/owner/pricing/traffic',
      () => ({ markup_bp: 0, markup_percent: 0, source: 'env' }),
    ),

  setTrafficMarkup: (markupBp: number) =>
    tryRequest<{ markup_bp: number; markup_percent: number; source: string }>(
      'PUT',
      '/api/owner/pricing/traffic',
      () => ({ markup_bp: markupBp, markup_percent: markupBp / 100, source: 'setting' }),
      { markup_bp: markupBp },
    ),

  // ---------- Balance adjust ----------

  adjustPartnerBalance: (partnerId: string, body: { amount_minor: number; reason: string; currency?: string }) =>
    tryRequest<{ new_balance_minor: number; currency: string }>(
      'POST',
      `/api/owner/partners/${partnerId}/balance/adjust`,
      () => ({ new_balance_minor: 0, currency: 'USD' }),
      { currency: 'USD', ...body },
    ),

  adjustClientBalance: (clientId: string, body: { amount_minor: number; reason: string; currency?: string }) =>
    tryRequest<{ new_balance_minor: number; currency: string }>(
      'POST',
      `/api/owner/partners/clients/${clientId}/balance/adjust`,
      () => ({ new_balance_minor: 0, currency: 'USD' }),
      { currency: 'USD', ...body },
    ),

  // ---------- Trash (soft-deleted) ----------

  trashedPartners: () =>
    tryRequest<Array<{ id: string; tg_id: number | null; display_name: string; banned_at: string }>>(
      'GET',
      '/api/owner/trash/partners',
      () => [],
    ),

  trashedBots: () =>
    tryRequest<
      Array<{
        id: string;
        tg_bot_id: number;
        username: string;
        title: string;
        partner_id: string;
        status: string;
        deleted_at: string;
      }>
    >('GET', '/api/owner/trash/bots', () => []),

  restoreBot: (botId: string) =>
    tryRequest<{ id: string; status: string }>(
      'POST',
      `/api/owner/trash/bots/${botId}/restore`,
      () => ({ id: botId, status: 'action' }),
    ),

  trashedPlans: () =>
    tryRequest<
      Array<{ id: string; name: string; days: number; devices: number; hidden_at: string }>
    >('GET', '/api/owner/trash/plans', () => []),

  // pg_stat_statements: топ медленных SQL-запросов для отладки/оптимизации.
  slowQueries: (limit = 30) =>
    tryRequest<{
      items: Array<{
        query: string;
        calls: number;
        mean_exec_ms: number;
        total_exec_ms: number;
        max_exec_ms: number;
        rows: number;
      }>;
      enabled: boolean;
    }>('GET', `/api/owner/perf/slow-queries?limit=${limit}`, () => ({ items: [], enabled: false })),

  resetSlowQueries: () =>
    tryRequest<void>('POST', '/api/owner/perf/slow-queries/reset', () => undefined),
};

export { ApiError };
